import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ColumnDef, Row } from '@tanstack/react-table';
import { PaginationMetadata } from 'api/common-schemas';
import { OrderItemType } from 'api/orders/schemas';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import { UsePaginatedTableReturn } from 'hooks/usePaginatedTable';
import { EmptyOrdersIndex } from 'screens/AccessExpressV3/Orders/EmptyResults';
import { OrderStatus } from '../utils';
import clsx from 'clsx';
import format from 'date-fns/format';
import ServiceTypeTag, {
  mapServiceTypeToTag,
  ServiceType,
} from 'screens/AccessExpressV3/BaseComponents/SeviceType';

interface OrderTableProps<T, M = PaginationMetadata> {
  sortingState: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  paginatedTableProps: Omit<UsePaginatedTableReturn<T, M>, 'data' | 'metadata'>;
  tablePageData: OrderItemType[];
  downloadTableFn: () => Promise<void>;
}

const OrderTable = <ResultData, MetadataType = PaginationMetadata>({
  sortingState,
  onSortingChange,
  paginatedTableProps,
  tablePageData,
  downloadTableFn,
}: OrderTableProps<ResultData, MetadataType>) => {
  const getOrdersIndexTableDef = (): ColumnDef<OrderItemType>[] => {
    const columns: ColumnDef<OrderItemType>[] = [
      {
        header: 'Order #',
        accessorKey: 'order_number',
        cell: ({ row }) => `# ${row.original.order_number}`,
      },
      {
        header: 'Address',
        accessorKey: 'address',
        cell: ({ row }) => (
          <div className="w-full">
            <p className="truncate">{row.original.address}</p>
            <p className="truncate text-xs text-content-base-subdued ">
              {row.original.city}
              {row.original.zip ? ',' : null} {row.original.zip}
            </p>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'g360_status',
        cell: ({ row }) => (
          <p className="flex w-full items-center gap-2 ">
            <span
              className={clsx('h-2.5 w-2.5 flex-none rounded-full', {
                'bg-green-300':
                  row.original.g360_status === OrderStatus.SUBMITTED,
                'bg-purple-300':
                  row.original.g360_status === OrderStatus.IN_PROGRESS,
                'bg-red-500':
                  row.original.g360_status === OrderStatus.NEEDS_ATTENTION,
                'bg-purple-600':
                  row.original.g360_status === OrderStatus.ON_HOLD,
                'bg-blue-500':
                  row.original.g360_status === OrderStatus.PENDING_GRANITE,
                'bg-yellow-300':
                  row.original.g360_status === OrderStatus.PENDING_SITE_SURVEY,
                'bg-orange-300':
                  row.original.g360_status === OrderStatus.CONSTRUCTION,
                'bg-accent-primary':
                  row.original.g360_status === OrderStatus.CONFIRMED,
                'bg-green-600':
                  row.original.g360_status === OrderStatus.CONFIGURED,
                'bg-teal-400':
                  row.original.g360_status === OrderStatus.COMPLETE,
              })}
            />
            <span className="truncate">{row.original.g360_status}</span>
          </p>
        ),
      },
      {
        header: () => <span className="truncate">Site name</span>,
        accessorKey: 'site_name',
        cell: ({ row }) => (
          <span className="truncate" title={row.original.site_name}>
            {row.original.site_name}
          </span>
        ),
      },
      {
        header: () => <span className="truncate">Service type</span>,
        accessorKey: 'product_name',
        cell: ({ row }) => (
          <ServiceTypeTag
            type={mapServiceTypeToTag(row.original.product_name as ServiceType)}
            className="max-w-full"
          />
        ),
      },
      {
        header: () => <span className="truncate">Date ordered</span>,
        accessorKey: 'date_ordered',
        cell: ({ row }) =>
          row.original.date_ordered
            ? format(new Date(row.original.date_ordered), 'MM/dd/yyyy')
            : '-- -- --',
      },
      {
        header: () => <span className="truncate">Confirmed</span>,
        accessorKey: 'date_confirmed',
        cell: ({ row }) =>
          row.original.date_confirmed
            ? format(new Date(row.original.date_confirmed), 'MM/dd/yyyy')
            : '-- -- --',
      },
    ];
    return columns;
  };

  const getRowClassName = (row: Row<OrderItemType>) => {
    if (row.original.g360_status === OrderStatus.NEEDS_ATTENTION)
      return '!bg-[#FF315E] !bg-opacity-[4%]';
    return '';
  };

  return (
    <ServerPaginatedTable
      data={tablePageData}
      columns={getOrdersIndexTableDef()}
      title="orders tracking table"
      sortingState={sortingState}
      onSortingChange={onSortingChange}
      showHeadersWhenEmpty={false}
      downloadTableFn={downloadTableFn}
      emptyDataElement={
        <EmptyOrdersIndex message="You haven’t placed any orders yet." />
      }
      tableContainerClassName="!grid-cols-[123px_repeat(6,minmax(122px,1fr))]"
      styleVariant="grid"
      getRowClassName={getRowClassName}
      {...paginatedTableProps}
    />
  );
};

export default OrderTable;
